/**
 * Lightweight helper to request a properly-sized, modern-format version of
 * an image hosted on Supabase Storage via its on-the-fly image transformation
 * endpoint. Non-Supabase URLs (e.g. Google Drive, external CDNs) are returned
 * unchanged so behavior is preserved everywhere.
 *
 * Usage: <img src={optimizedImage(url, { width: 56, quality: 75 })} />
 */
export function optimizedImage(
  url: string | undefined | null,
  opts: { width: number; height?: number; quality?: number; format?: "webp" | "origin"; resize?: "cover" | "contain" } = { width: 128 }
): string {
  if (!url) return "";
  // Only transform Supabase Storage public object URLs.
  // Pattern: /storage/v1/object/public/<bucket>/<path>
  const marker = "/storage/v1/object/public/";
  const idx = url.indexOf(marker);
  if (idx === -1) return url;

  // Skip if already a render/image URL or if it's an SVG (no transform needed).
  if (url.includes("/storage/v1/render/image/public/")) return url;
  if (/\.svgi?$/i.test(url.split("?")[0])) return url;

  const transformedBase =
    url.slice(0, idx) + "/storage/v1/render/image/public/" + url.slice(idx + marker.length);

  const dpr = typeof window !== "undefined" && window.devicePixelRatio ? Math.min(2, Math.ceil(window.devicePixelRatio)) : 1;
  const params = new URLSearchParams();
  params.set("width", String(Math.round(opts.width * dpr)));
  if (opts.height) params.set("height", String(Math.round(opts.height * dpr)));
  // Default to "contain" so uploaded media (category icons, gallery thumbnails,
  // product photos) is never zoom-cropped — it always fits the frame with the
  // correct aspect ratio. Callers that need cropped fills (e.g. circular
  // avatars) should pass resize: "cover" explicitly.
  params.set("resize", opts.resize ?? "contain");
  params.set("quality", String(opts.quality ?? 75));
  // Note: Supabase's image transformer only accepts format=origin. WebP is
  // negotiated automatically via the browser's Accept header, so we omit the
  // format param entirely for modern delivery.
  if (opts.format === "origin") params.set("format", "origin");

  const sep = transformedBase.includes("?") ? "&" : "?";
  return `${transformedBase}${sep}${params.toString()}`;
}
